/*
 * Product · Buy Box (sections/pdp-buybox.liquid).
 *
 * - Gallery: a scroll-snap track (swipe on touch) with prev/next arrows that
 *   loop like the Replo carousel (prev on the first image goes to the last;
 *   that arrow is invisible on the first image). The thumbnail strip follows
 *   the current image (active thumb scrolled to the start), its own arrows
 *   step the gallery and dim at either end.
 * - Accordions: one open at a time, like Replo.
 * - Jump links and the rating line: smooth scroll to the target anchor.
 * - Add to cart: posts the form to /cart/add.js, then opens the cart drawer
 *   the same way assets/home-bestseller.js does (Essential Cart app, then the
 *   theme's #cart-modal, then the cart page). Without fetch the form posts.
 * - Variant picker (only rendered for multi-variant products): keeps price,
 *   compare-at price, monthly line and button state in sync.
 * - Bread Pay: the live page's loader. Bread's cart.js renders its checkout
 *   button into #bread-checkout-btn-product (invisible, over the financing
 *   box) so a tap opens Bread's modal.
 * - App sticky add-to-cart (Essential Cart): kept hidden until the visitor
 *   has scrolled past the buy box.
 */
(() => {
  if (window.pdpBuybox) return;
  window.pdpBuybox = { version: 1 };

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const behavior = () => (reducedMotion.matches ? 'auto' : 'smooth');

  /* Gallery ------------------------------------------------------------- */

  function initGallery(root) {
    const carousel = root.querySelector('[data-pb-carousel]');
    if (!carousel || carousel.dataset.pbReady) return;
    carousel.dataset.pbReady = 'true';

    const viewport = carousel.querySelector('[data-pb-viewport]');
    if (!viewport) return;
    const slides = Array.from(viewport.children);
    const last = slides.length - 1;
    const prev = carousel.querySelector('[data-pb-prev]');
    const next = carousel.querySelector('[data-pb-next]');
    const thumbsBox = carousel.querySelector('[data-pb-thumbs]');
    const thumbs = Array.from(carousel.querySelectorAll('[data-pb-thumb]'));
    const tprev = carousel.querySelector('[data-pb-tprev]');
    const tnext = carousel.querySelector('[data-pb-tnext]');
    const status = carousel.querySelector('[data-pb-status]');
    let current = 0;
    let timer = 0;

    const offset = (index) => slides[index].offsetLeft - slides[0].offsetLeft;

    function nearestIndex() {
      const x = viewport.scrollLeft;
      let best = 0;
      let distance = Infinity;
      slides.forEach((slide, index) => {
        const d = Math.abs(offset(index) - x);
        if (d < distance) {
          distance = d;
          best = index;
        }
      });
      return best;
    }

    // Active thumbnail at the start of its strip (scrolls the strip only).
    function revealThumb(index) {
      if (!thumbsBox || !thumbs[index]) return;
      const left = thumbs[index].offsetLeft - thumbs[0].offsetLeft;
      thumbsBox.scrollTo({ left, behavior: behavior() });
    }

    function paint(index) {
      current = index;
      if (prev) prev.classList.toggle('is-start', index === 0);
      if (tprev) tprev.classList.toggle('is-dim', index === 0);
      if (tnext) tnext.classList.toggle('is-dim', index === last);
      thumbs.forEach((thumb, i) => {
        if (i === index) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      if (status) status.textContent = `Slide ${index + 1} of ${slides.length}`;
      revealThumb(index);
    }

    function goTo(index) {
      let target = index;
      let wrapped = false;
      if (target < 0) {
        target = last;
        wrapped = true;
      }
      if (target > last) {
        target = 0;
        wrapped = true;
      }
      // Looping jumps straight there instead of sweeping past every image.
      viewport.scrollTo({ left: offset(target), behavior: wrapped ? 'auto' : behavior() });
      if (target !== current) paint(target);
    }

    viewport.addEventListener(
      'scroll',
      () => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          const index = nearestIndex();
          if (index !== current) paint(index);
        }, 80);
      },
      { passive: true }
    );

    if (prev) prev.addEventListener('click', () => goTo(current - 1));
    if (next) next.addEventListener('click', () => goTo(current + 1));
    if (tprev) tprev.addEventListener('click', () => current > 0 && goTo(current - 1));
    if (tnext) tnext.addEventListener('click', () => current < last && goTo(current + 1));
    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => goTo(Number(thumb.dataset.pbThumb)));
    });
    viewport.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        goTo(current + (event.key === 'ArrowRight' ? 1 : -1));
      }
    });

    // Re-align to the current image when the layout changes (breakpoints).
    let resizeTimer = 0;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        viewport.scrollTo({ left: offset(current), behavior: 'auto' });
        revealThumb(current);
      }, 150);
    });

    paint(0);
  }

  /* Accordions ------------------------------------------------------------ */

  function initAccordions(root) {
    const items = Array.from(root.querySelectorAll('[data-pb-acc]'));
    items.forEach((item) => {
      const button = item.querySelector('[data-pb-acc-btn]');
      if (!button || button.dataset.pbReady) return;
      button.dataset.pbReady = 'true';
      button.addEventListener('click', () => {
        const open = button.getAttribute('aria-expanded') !== 'true';
        items.forEach((other) => setOpen(other, other === item ? open : false));
      });
    });
  }

  function setOpen(item, open) {
    const button = item.querySelector('[data-pb-acc-btn]');
    const panel = item.querySelector('[data-pb-acc-panel]');
    if (!button || !panel) return;
    button.setAttribute('aria-expanded', String(open));
    panel.hidden = !open;
    item.classList.toggle('is-open', open);
  }

  /* Jump links ------------------------------------------------------------ */

  function initScrollLinks(root) {
    root.querySelectorAll('[data-pb-scroll]').forEach((link) => {
      if (link.dataset.pbReady) return;
      link.dataset.pbReady = 'true';
      link.addEventListener('click', (event) => {
        const id = (link.getAttribute('href') || '').replace(/^#/, '');
        const target = id && document.getElementById(id);
        if (!target) return; // let the browser handle it
        event.preventDefault();
        const top = target.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({ top, behavior: behavior() });
        if (history.replaceState) history.replaceState(null, '', `#${id}`);
      });
    });
  }

  /* Cart ------------------------------------------------------------------ */

  async function openCartDrawer() {
    const essential = window.essentialCartApi;
    if (essential && typeof essential.openCart === 'function') {
      if (typeof essential.refreshCart === 'function') await essential.refreshCart();
      essential.openCart();
      return;
    }
    const themeDrawer = document.getElementById('cart-modal');
    const trigger = document.querySelector('[target="#cart-modal"], [data-target="#cart-modal"]');
    if (themeDrawer && trigger) {
      if (typeof themeDrawer.reloadContent === 'function') await themeDrawer.reloadContent();
      trigger.click();
      return;
    }
    const base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    window.location.assign(`${base}cart`);
  }

  function initBuy(root) {
    const form = root.querySelector('form.pdp-buybox__form');
    if (!form || form.dataset.pbReady) return;
    form.dataset.pbReady = 'true';

    const button = form.querySelector('[data-pb-add]');
    const label = form.querySelector('[data-pb-add-label]');
    const errorBox = form.querySelector('[data-pb-error]');
    const idInput = form.querySelector('[data-pb-variant-id]');
    const select = form.querySelector('[data-pb-variant-select]');
    let busy = false;

    function showError(message) {
      if (!errorBox) return;
      errorBox.textContent = message || '';
      errorBox.hidden = !message;
    }

    form.addEventListener('submit', async (event) => {
      if (!window.fetch || !window.FormData) return; // plain /cart/add post
      event.preventDefault();
      if (busy || (button && button.disabled)) return;
      busy = true;
      if (button) button.setAttribute('aria-busy', 'true');
      showError('');

      const base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      let response;
      try {
        response = await fetch(`${base}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(form),
        });
      } catch (error) {
        form.submit(); // network trouble: normal form post
        return;
      }

      try {
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.description || data.message || 'Could not add to cart.');
        }
        await openCartDrawer();
      } catch (error) {
        showError(error.message);
      } finally {
        busy = false;
        if (button) button.removeAttribute('aria-busy');
      }
    });

    if (select) {
      select.addEventListener('change', () => {
        const option = select.selectedOptions[0];
        if (!option) return;
        if (idInput) idInput.value = option.value;

        const price = root.querySelector('[data-pb-price]');
        if (price) price.textContent = option.dataset.price;
        const compare = root.querySelector('[data-pb-compare]');
        if (compare) {
          compare.textContent = option.dataset.compare || '';
          compare.hidden = !option.dataset.compare;
        }

        const available = option.dataset.available === 'true';
        if (button) button.disabled = !available;
        if (label && button) label.textContent = available ? button.dataset.label : button.dataset.soldOutLabel;
        updateMonthly(root, Number(option.dataset.priceCents), Number(option.dataset.compareCents));
        showError('');

        if (window.history && window.history.replaceState) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', option.value);
          window.history.replaceState(null, '', url.toString());
        }
      });
    }
  }

  function updateMonthly(root, priceCents, compareCents) {
    const text = root.querySelector('[data-pb-bread-text]');
    if (!text || !priceCents) return;
    const months = Number(text.dataset.months) || 36;
    const template = text.dataset.template || 'As low as [monthly]/mo';
    const monthly = (cents) => `$${Math.ceil(cents / 100 / months)}`;
    const line = text.querySelector('.pdp-buybox__bread-line');
    if (!line) return;
    line.textContent = template.replace('[monthly]', monthly(priceCents));
    if (compareCents && compareCents > priceCents) {
      const was = document.createElement('span');
      was.className = 'pdp-buybox__bread-was';
      // Only the old amount, e.g. "$320/mo", not the whole "As low as" line.
      const suffix = template.includes('[monthly]') ? template.split('[monthly]').slice(1).join('[monthly]') : '';
      was.textContent = monthly(compareCents) + suffix;
      line.append(' ', was);
    }
  }

  /* Bread Pay ------------------------------------------------------------- */

  const bread = { handle: null, injected: false };
  const SDK_ID = 'bread-cart-sdk-script'; // reserved by cart.js, never used on our loader
  const ANCHOR = '[data-testid="bread-placement-bread-checkout-btn-product"]';
  const hasAnchor = () => Boolean(document.querySelector(ANCHOR));

  // cart.js looks the product up from the last URL segment. On a product page
  // that is already right; this only matters when the URL has no handle
  // (e.g. a theme-editor preview), where the lookup goes to the real product.
  function installXhrRewrite() {
    const Native = window.XMLHttpRequest;
    if (!Native || Native.pbBreadPatched || Native.hbBreadPatched) return;
    const segment = window.location.pathname.replace(/\/$/, '').split('/').pop();
    const bogusA = `/products/${segment}.js`;
    const bogusB = '/products/.js';
    let done = false;

    function Patched() {
      const instance = new Native();
      const open = instance.open.bind(instance);
      instance.open = function patchedOpen(method, url, ...rest) {
        let target = url;
        try {
          if (!done && bread.handle && typeof url === 'string') {
            const path = url.split('?')[0];
            if ((path === bogusA || path === bogusB) && path !== `/products/${bread.handle}.js`) {
              target = `/products/${bread.handle}.js`;
              done = true;
            }
          }
        } catch (error) {
          /* keep the original URL */
        }
        return open(method, target, ...rest);
      };
      return instance;
    }
    Patched.pbBreadPatched = true;
    Object.keys(Native).forEach((key) => {
      try {
        Patched[key] = Native[key];
      } catch (error) {
        /* read-only static */
      }
    });
    window.XMLHttpRequest = Patched;
    setTimeout(() => {
      if (window.XMLHttpRequest === Patched) window.XMLHttpRequest = Native;
    }, 20000);
  }

  function injectCartJs(shopId) {
    if (bread.injected) return;
    bread.injected = true;
    if (window.BreadPayments || document.getElementById(SDK_ID)) return;
    const script = document.createElement('script');
    script.src = `https://callback.redirect.pp-prod-ads.breadgateway.net/api/shopify-plugin-backend/messaging-app/cart.js?shopId=${encodeURIComponent(shopId)}&_t=${Date.now()}`;
    document.documentElement.appendChild(script);
  }

  // Campaign pricing (Theme settings > Campaign pricing): Bread's pop-up
  // reads the price from /products/<handle>.js. While a campaign price applies
  // (data-campaign-price on the section, in cents) that response gets the
  // campaign price, so Bread finances the Discounted Price. Any parse problem
  // returns the response untouched.
  function installCampaignPrice(handle, priceCents) {
    const Native = window.XMLHttpRequest;
    if (!Native || !handle || !(priceCents > 0) || Native.campaignPricePatched) return;
    const proto = Native.prototype;
    const textDesc = Object.getOwnPropertyDescriptor(proto, 'responseText');
    const respDesc = Object.getOwnPropertyDescriptor(proto, 'response');
    if (!textDesc || !textDesc.get) return;
    const target = `/products/${handle}.js`;
    const nativeOpen = proto.open;
    proto.open = function campaignOpen(method, url, ...rest) {
      try {
        this.campaignPrice = typeof url === 'string' && url.split('?')[0].endsWith(target);
      } catch (error) {
        this.campaignPrice = false;
      }
      return nativeOpen.call(this, method, url, ...rest);
    };
    const rewrite = (raw) => {
      try {
        const data = JSON.parse(raw);
        const lower = (value) => (typeof value === 'number' && value > priceCents ? priceCents : value);
        if (typeof data.price === 'number' && data.price > priceCents) {
          data.compare_at_price = Math.max(data.compare_at_price || 0, data.price);
        }
        data.price = lower(data.price);
        data.price_min = lower(data.price_min);
        data.price_max = lower(data.price_max);
        (data.variants || []).forEach((variant) => {
          if (typeof variant.price === 'number' && variant.price > priceCents) {
            variant.compare_at_price = Math.max(variant.compare_at_price || 0, variant.price);
            variant.price = priceCents;
          }
        });
        return JSON.stringify(data);
      } catch (error) {
        return raw;
      }
    };
    Object.defineProperty(proto, 'responseText', {
      configurable: true,
      get() {
        const raw = textDesc.get.call(this);
        return this.campaignPrice && this.readyState === 4 ? rewrite(raw) : raw;
      },
    });
    if (respDesc && respDesc.get) {
      Object.defineProperty(proto, 'response', {
        configurable: true,
        get() {
          const value = respDesc.get.call(this);
          return this.campaignPrice && this.readyState === 4 && typeof value === 'string' ? rewrite(value) : value;
        },
      });
    }
    Native.campaignPricePatched = true;
  }

  function initBread(root) {
    const widget = root.querySelector('[data-pb-bread]');
    if (!widget || widget.dataset.pbReady) return;
    widget.dataset.pbReady = 'true';
    const shopId = widget.dataset.shopId;
    if (!shopId) return;
    bread.handle = root.dataset.productHandle || bread.handle;
    installCampaignPrice(bread.handle, Number(root.dataset.campaignPrice));

    const onProductUrl = window.location.pathname.indexOf('/products/') !== -1;
    if (!onProductUrl) installXhrRewrite();
    injectCartJs(shopId);

    // Tapped before Bread rendered its anchor: kick off the injection again.
    const overlay = widget.querySelector('#bread-checkout-btn-product');
    if (overlay) {
      overlay.addEventListener('click', () => {
        if (!hasAnchor()) {
          bread.injected = false;
          if (!onProductUrl) installXhrRewrite();
          injectCartJs(shopId);
        }
      });
    }
  }

  /* App sticky add-to-cart ------------------------------------------------ */

  // The Essential Cart app's sticky bar (#sticky-cart-portal) shows from the
  // top of the page here because it can't find a main Add to Cart it
  // recognises. Keep it hidden (html.pdp-sticky-atc-hidden, see
  // pdp-buybox.css) while the buy box is on screen or still below it; it
  // shows once the visitor has scrolled past the buy box.
  // One getBoundingClientRect per scroll event is cheap, so no frame
  // throttling (rAF also stalls in background tabs).
  let stickyRoot = null;

  function updateStickyAtc() {
    const visible = stickyRoot && stickyRoot.isConnected && stickyRoot.getBoundingClientRect().bottom > 0;
    document.documentElement.classList.toggle('pdp-sticky-atc-hidden', Boolean(visible));
  }

  function initStickyAtc(root) {
    const first = !stickyRoot;
    stickyRoot = root;
    updateStickyAtc();
    if (!first) return;
    window.addEventListener('scroll', updateStickyAtc, { passive: true });
    window.addEventListener('resize', updateStickyAtc);
  }

  /* Boot ------------------------------------------------------------------ */

  function init(root) {
    if (!root) return;
    initGallery(root);
    initAccordions(root);
    initScrollLinks(root);
    initBuy(root);
    initBread(root);
    initStickyAtc(root);
  }

  function initAll() {
    document.querySelectorAll('[data-pb-root]').forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target.querySelector('[data-pb-root]'));
  });

  // Theme editor: open an accordion when its block is selected.
  document.addEventListener('shopify:block:select', (event) => {
    const item = event.target.closest ? event.target.closest('[data-pb-acc]') : null;
    if (!item) return;
    const root = item.closest('[data-pb-root]');
    if (!root) return;
    root.querySelectorAll('[data-pb-acc]').forEach((other) => setOpen(other, other === item));
  });
})();
